import {Controller, FieldError, Path, useFormContext} from "react-hook-form";
import Row from "react-bootstrap/Row";
import Form from "react-bootstrap/Form";
import {MANDATORY_ICON} from "../FormComponents";
import Col from "react-bootstrap/Col";
import React, {ReactNode, useState} from "react";
import {Typeahead} from "react-bootstrap-typeahead";

type Option = {
  key: string | number;
  label: string;
  disabled?: boolean
}

/**
 * Props for this component
 */
interface DropdownProps<T extends Record<string, any>> {
  label: string;
  helpText?: ReactNode;
  content: Option[];
  defaultValue?: Option;
  lazyLoadTrigger?: () => void;
  error: FieldError | undefined;
  fieldId: Path<T>;
  disabled?: boolean;
  mandatory?: boolean;
  customValidation?: (v: any) => true | string;
  typeahead?: boolean;
  sideAction?: ReactNode;
  onChange?: (value: string | number | undefined) => void;
}

/**
 * This is a component that will add a dropdown box. It was designed to be used as part of a form panel. You may
 * pass the following props:
 *
 * @param label The text to show before the dropdown. (By default this will show to the left of the input
 * @param helpText Subtext that shows on top of the question
 * @param content A string array with the options that we want to show in the dropdown
 * @param defaultValue <i>Optional</i> The default value that will be selected
 * @param lazyLoadTrigger <i>Optional</i> This is a trigger to provide our dropdown with lazy loading functionality
 * @param error The fieldError from the React useForm specific for this field
 * @param fieldId The ID of the field as defined in the form interface
 * @param disabled <i>Optional</i> A boolean to mark the field as disabled
 * @param mandatory <i>Optional</i> A boolean to mark the field as mandatory
 * @param functionForExtraValidation <i>Optional</i> A function where we can provide the component some more specific validations
 * @param typeahead <i>Optional</i> A boolean to enable the typeahead function for this dropdown
 * @param sideAction <i>Optional</i> If more action is needed to be associated with this dropdown it can be passed as a React component here
 * @param onChange <i>Optional</i> Function to run on change
 */
export default function Dropdown<T extends Record<string, any>>({
                                                                  label,
                                                                  helpText,
                                                                  content,
                                                                  defaultValue,
                                                                  lazyLoadTrigger,
                                                                  error,
                                                                  fieldId,
                                                                  disabled = false,
                                                                  mandatory = false,
                                                                  customValidation,
                                                                  typeahead = false,
                                                                  sideAction,
                                                                  onChange
                                                                }: DropdownProps<T>) {
  const {register, control} = useFormContext<T>();
  const [selected, setSelected] = useState<Option[]>(
    defaultValue ? [defaultValue] : []
  );

  // This is to be used when generating data-test-ids
  const fieldIdForDataTest = label.toLowerCase().replace(" ", "-");

  return <Form.Group as={Row} className="mb-2" controlId={`formHorizontal${fieldId}`}>
    {helpText && <Row>
      <Col sm={2}></Col>
      <Col sm={10} className={"question-help-text"}>
        {helpText}
      </Col>
    </Row>}
    <Row>
      <Form.Label column sm={2}>
        {label} {mandatory && <MANDATORY_ICON/>}
      </Form.Label>
      <Col>
        {typeahead ?
          <Controller
            name={fieldId}
            control={control}
            defaultValue={defaultValue?.key as any}
            rules={{
              required: mandatory ? `${label === "" ? "This field" : label} is required` : false,
              validate: (v: any) => {
                // Empty box check
                if (mandatory && (v === null || v === undefined || v === "" || v === -1)) {
                  return `${label === "" ? "This field" : label} is required`;
                }

                if (customValidation) {
                  return customValidation(v);
                }
                return true;
              }
            }}
            render={({field}) => (
              <Typeahead
                id={fieldId}
                disabled={disabled}
                options={content}
                labelKey={"label"}
                selected={selected}
                onChange={(selected) => {
                  const option = selected[0] as Option | undefined;

                  setSelected(option ? [option] : []);
                  field.onChange(option?.key ?? null);
                  if (onChange) {
                    onChange(option?.key);
                  }
                }}
                isInvalid={!!error}
                data-testid={fieldIdForDataTest}
                onMenuToggle={e => {
                  defaultValue = undefined;
                  if (content.length < 1 && lazyLoadTrigger) {
                    lazyLoadTrigger();
                  }
                }}
              />
            )}/>
          :
          <Form.Select
            disabled={disabled}
            {...register(fieldId, {
              required: mandatory ? `${label} is required` : false,
              validate: customValidation ?
                (v: any) => customValidation(v) :
                undefined
            })}
            isInvalid={!!error}
            data-testid={fieldIdForDataTest}
            onClick={e => {
              if (content.length < 1 && lazyLoadTrigger) {
                lazyLoadTrigger();
              }
            }}
            onChange={(e) => {
              if (onChange) {
                onChange(e.target.value as string | number | undefined);
              }
            }}>
            {/* Initial empty value */}
            <option value={defaultValue ? defaultValue.key : ""}>{defaultValue?.label}</option>
            {(content.length == 0) ?
              <option value={""}>Loading, please wait...</option> :
              <>
                {content.map((option) => (option !== defaultValue &&
                    <option value={option.key} key={option.key} disabled={option.disabled}>{option.label}</option>))}
              </>}
          </Form.Select>
        }
        <Form.Control.Feedback type="invalid" data-testid={`${fieldIdForDataTest}-error`}>
          {error?.message}
        </Form.Control.Feedback>
      </Col>
      {(sideAction && !disabled) && <>
        {sideAction}</>}
    </Row>
  </Form.Group>
    ;
}